import DashboardHeader from "../dashboardheader/DashboardHeader";
import {
    DatePicker,
    Button,
    Row,
    Col,
    Card,
    Table,
    Select,
    Modal,
    Input,
    Descriptions,
} from "antd";
import {
    SearchOutlined,
    DownloadOutlined,
    ScheduleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";


const { Option } = Select;

export default function ViewEmpAttendance({ employee }) {
    const navigate = useNavigate();
    const { authData, backUrl } = useContext(AuthContext);

    const [attendanceData, setAttendanceData] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    // Lead details modal state
    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter state
    const [filterFromDate, setFilterFromDate] = useState(null);
    const [filterToDate, setFilterToDate] = useState(null);
    const [filterStatus, setFilterStatus] = useState("");
    const [filterRemarks, setFilterRemarks] = useState("");

    // Modal state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [requestReason, setRequestReason] = useState("");
    const [attendanceId, setAttendanceId] = useState(0);

    // Build API URL with all query parameters
    const buildApiUrl = () => {
        const params = new URLSearchParams();

        params.append('page', currentPage);
        params.append('page_size', pageSize);

        if (filterFromDate) {
            params.append('from_date', dayjs(filterFromDate).format('YYYY-MM-DD'));
        }
        if (filterToDate) {
            params.append('to_date', dayjs(filterToDate).format('YYYY-MM-DD'));
        }
        if (filterStatus) {
            params.append('status', filterStatus);
        }
        if (filterRemarks) {
            params.append('remarks', filterRemarks);
        }

        if (employee?.id) {
            params.append('employee_id', employee.id);
        }

        return `${backUrl}/api/hr/attendance/?${params.toString()}`;
    };

    // Fetch attendance data from backend
    const fetchData = async () => {
        setLoading(true);
        try {
            const url = buildApiUrl();
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });

            const formattedData = res.data.results.map((item) => ({
                ...item,
                Date: dayjs(item.Date || item.date || item.start_time).format("DD/MM/YYYY"),
                consideration_requests: item.consideration_requests || [],
                permissions: item.permissions || [],
            }));

            setAttendanceData(formattedData);
            setTotalRecords(res.data.count || 0);
        } catch (err) {
            console.error("Error fetching attendance data", err);
            alert("Failed to fetch attendance data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when pagination or filters change
    useEffect(() => {
        fetchData();
    }, [currentPage, pageSize, filterFromDate, filterToDate, filterStatus, filterRemarks]);

    // Reset to page 1 when filters change
    const handleFilterChange = (filterSetter, value) => {
        filterSetter(value);
        setCurrentPage(1);
    };

    // Table columns definition
    const columns = [
        { title: "Employee Code", dataIndex: "employee_code", key: "employee_code" },
        { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
        { title: "Date", dataIndex: "date", key: "date" },
        {
            title: "Work Location",
            dataIndex: "work_location",
            key: "work_location",
            render: (site, record) =>
                (site === "On-Duty" || (record.lead_details && record.lead_details.length > 0)) ? (
                    <>
                        <p style={{ marginBottom: 4 }}>{site}</p>
                        <p>{record.check_in_out_mode}</p>
                        {record?.onduty_type_check_in && (
                            <p style={{ marginBottom: 4, fontSize: "12px", color: "#666" }}>
                                {record.onduty_type_check_in}
                            </p>
                        )}
                        {
                            (record?.lead_details && record.lead_details.length > 0) && (
                                <a
                                    onClick={() => {
                                        setSelectedLeadDetails(record.lead_details);
                                        setShowLead(true);
                                    }}
                                    style={{ color: "#1677ff", cursor: "pointer", display: "block", marginTop: "4px" }}
                                >
                                    Lead Details
                                </a>
                            )}
                    </>
                ) : (
                    <p>{site}</p>
                ),
        },
        { title: "Punch-In", dataIndex: "punch_in", key: "punch_in" },
        { title: "Check-In", dataIndex: "check_in", key: "check_in" },
        {
            title: "Check-In Photo",
            dataIndex: "checkInPhoto",
            key: "checkInPhoto",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Check-In Location",
            dataIndex: "check_in_location",
            key: "check_in_location",
            width: 250,
            render: (text, record) => (
                <p style={{ margin: 0 }}>
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${record.check_in_latitude},${record.check_in_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff", textDecoration: "none", cursor: "pointer" }}
                    >
                        {text}
                    </a>
                </p>
            ),
        },
        {
            title: "Check-In Site",
            key: "check_in_site",
            render: (_, record) => (
                <>
                    {record.check_in_site || "-"}
                    {record.onduty_type_check_in && (
                        <> ({record.onduty_type_check_in})</>
                    )}
                </>
            ),
        },
        { title: "Punch-Out", dataIndex: "punch_out", key: "punch_out" },
        { title: "Check-Out", dataIndex: "check_out", key: "check_out" },
        {
            title: "Check-Out Photo",
            dataIndex: "checkOutPhoto",
            key: "checkOutPhoto",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-Out"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },

        {
            title: "Check-Out Location",
            dataIndex: "check_out_location",
            key: "check_out_location",
            width: 250,
            render: (text, record) => {
                const isBSSInternal = record.check_out_mode === "BSS_INTERNAL";
                return (
                    <div>
                        {(record.check_out_latitude && record.check_out_longitude) ? (
                            <a
                                href={`https://www.google.com/maps/search/?api=1&query=${record.check_out_latitude},${record.check_out_longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1677ff", textDecoration: "none", cursor: "pointer" }}
                            >
                                {text || "View Location"}
                            </a>
                        ) : (
                            <span>{text || "-"}</span>
                        )}

                        {/* BSS Auto-Checkout badge — only shown when checkout was triggered by BSS internally */}
                        {isBSSInternal && (
                            <div style={{
                                marginTop: 4,
                                padding: "2px 8px",
                                background: "#fff7e6",
                                border: "1px solid #ffa940",
                                borderRadius: 4,
                                fontSize: 11,
                                color: "#d46b08",
                                fontWeight: 500,
                            }}>
                                ⚠ {record.sales_checkout_remarks || "Auto Checkout - Out of Radius"}
                            </div>
                        )}

                        {/* Manual checkout remark (if provided by the employee) */}
                        {!isBSSInternal && record.check_out_remarks && (
                            <div style={{
                                marginTop: 4,
                                padding: "2px 8px",
                                background: "#f6ffed",
                                border: "1px solid #b7eb8f",
                                borderRadius: 4,
                                fontSize: 11,
                                color: "#389e0d",
                                fontWeight: 500,
                            }}>
                                💬 {record.check_out_remarks}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Check-Out Site",
            key: "check_out_site",
            render: (_, record) => {
                const isBSSInternal = record.check_out_mode === "BSS_INTERNAL";
                return (
                    <>
                        {record.check_out_site || "-"}
                        {isBSSInternal ? (
                            <div style={{
                                display: "inline-block",
                                marginLeft: 4,
                                padding: "1px 6px",
                                background: "#fff7e6",
                                border: "1px solid #ffa940",
                                borderRadius: 4,
                                fontSize: 10,
                                color: "#d46b08",
                                fontWeight: 600,
                                verticalAlign: "middle",
                            }}>
                                BSS Auto
                            </div>
                        ) : (
                            record.check_out_mode && (
                                <p style={{ margin: 0, fontSize: 11, color: "#888" }}>
                                    {record.check_out_mode}
                                </p>
                            )
                        )}
                        {record.onduty_type_check_out && (
                            <> ({record.onduty_type_check_out})</>
                        )}
                    </>
                );
            },
        },
        { title: "First In", dataIndex: "first_in", key: "first_in" },
        { title: "Last Out", dataIndex: "last_out", key: "last_out" },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            render: (remarks) => formatRemarks(remarks)
        },
        {
            title: "Attendance Status",
            key: "status",
            dataIndex: "status",
            render: (status, record) => {
                const permissions = record.permissions || [];
                const considerationRequests = record.consideration_requests || [];

                // Find the Auto-Deduction permission specifically
                const autoPermission = permissions.find(
                    (p) => p.type === "Auto-Deduction"
                );

                // Only flag as auto-permission if status is PERMISSION AND an Auto-Deduction entry exists
                const isAutoPermission = status === "PERMISSION" && !!autoPermission;

                const statusColorMap = {
                    PRESENT: "#00AF50",                        // dark green
                    ABSENT: "#FE0000",                         // red
                    "GRACE - LATE IN": "#FFFECB",             // light mint green
                    "GRACE - EARLY OUT": "#FFFECB",
      "GRACE – EARLY OUT": "#FFFECB",           // light mint green
                    "LATE IN": "#FFFECB",                      // light yellow
                    "EARLY OUT": "#FFFECB",                    // light yellow
                    "SHORTFALL PRESENT": "#298988",            // dark teal
                    "HALF DAY - SHORTFALL PRESENT": "#58BABB", // medium teal/cyan
                    "SHORT HOURS": "#DED9C5",                  // beige/tan
                    "MISPUNCH OUT": "#BFBFBF",                 // medium gray
                    "MISPUNCH IN": "#BFBFBF",                  // medium gray
                    PERMISSION: "#98FF66",                     // bright lime green
                    HOLIDAY: "#F79647",                        // orange
                    SUNDAY: "#FFFF00",                         // pure yellow
                    COMPOFF: "#1FA2CC",                        // sky/cyan blue
                    DEFAULT: "white",
                    WEEKOFF: "#FFFF00",                        // pure yellow
                    TECHNICAL_ISSUE: "#303343",
                    CASUAL: "#CB00CC",                         // magenta/purple
                    SICK: "#FF0066",                           // hot pink / deep pink
                    "HALF DAY - CASUAL LEAVE": "#FE66CB",             // light pink
                    "HALF DAY - SICK LEAVE": "#B1A1C8",
                    "LOP": "#bf4406",
                    "HALF DAY - COMPOFF": "#66ffff",
                    "HALF DAY - WEEKOFF": "#ffcc00",
                };

                const statusColor =
                    statusColorMap[record.remarks?.toUpperCase()] ||
                    statusColorMap[status] ||
                    "#000";

                // Determine the main display label
                const displayStatus =
                    status === "LEAVE"
                        ? record.leavetype
                        : status === "CASUAL"
                            ? "Casual Leave"
                            : status === "SICK"
                                ? "Sick Leave"
                                : status === "SUNDAY"
                                    ? "Week Off"
                                    : status || "-";

                return (
                    <div style={{ textAlign: "center" }}>
                        {/* Main status badge */}
                        <div style={{ fontWeight: 600, color: statusColor }}>
                            {displayStatus}
                        </div>

                        {/* If status changed to PRESENT via a consideration request, show info */}
                        {status === "Present" && considerationRequests.length > 0 && (
                            <>
                                <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                    Changed via consideration
                                </small>
                                <br />
                                <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                    Remarks: {considerationRequests[0]?.remarks || "-"}
                                </small>
                                <br />
                            </>
                        )}

                        {/* ─── Auto-Deduction block ─── */}
                        {/* {isAutoPermission && (
                            <>
                                <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                    Auto-generated permission ({autoPermission.hours_requested} hr)
                                </small>
                                <br />

                                {considerationRequests.length === 0 ? (
                                    <Button
                                        onClick={() => {
                                            setAttendanceId(record.id);
                                            setIsModalVisible(true);
                                        }}
                                        className="mt-1"
                                        style={{
                                            backgroundColor: "#0e497b",
                                            color: "white",
                                            height: "28px",
                                            fontSize: "12px",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        Request
                                    </Button>
                                ) : (
                                    <>
                                        <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                            Request Status
                                        </small>
                                        <br />
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                fontSize: "12px",
                                                color:
                                                    considerationRequests[0]?.status === "Approved"
                                                        ? "#16a34a"
                                                        : considerationRequests[0]?.status === "Rejected"
                                                            ? "#dc2626"
                                                            : "#d97706",
                                            }}
                                        >
                                            {considerationRequests[0]?.status || "-"}
                                        </div>
                                        {considerationRequests[0]?.remarks && (
                                            <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                                Remarks: {considerationRequests[0].remarks}
                                            </small>
                                        )}
                                    </>
                                )}
                            </>
                        )} */}
                    </div>
                );
            },
        },
    ];

    // Transform raw API data → flat objects consumed by <Table>
    const dataSource = attendanceData.map((item) => ({
        key: `${item.EmployeeCode || item.employee_code || item.id}_${item.Date || item.date || item.start_time}`,
        id: item.id,
        employee_code: item.EmployeeCode || item.employee_code || "-",
        employee_name: item.EmployeeName || item.employee_name || "-",
        date: item.Date || item.date || (item.start_time ? dayjs(item.start_time).format("DD/MM/YYYY") : "-"),
        work_location: item.WorkLocation || item.work_location || item.onduty_type || "-",
        check_in: item.CheckIn || item.check_in || "-",
        punch_in: item.PunchIn || item.punch_in || "-",
        check_out: item.CheckOut || item.check_out || "-",
        punch_out: item.PunchOut || item.punch_out || "-",
        first_in: item.FirstInType || item.first_in || "-",
        last_out: item.LastOutType || item.last_out || "-",
        status: item.Status === "SUNDAY" || item.status === "SUNDAY" || item.Status === "Week Off" || item.status === "Week Off"
            ? "Week Off"
            : item.Status || item.status || "-",
        remarks: item.AttendanceStatus || item.remarks || "-",
        checkInPhoto: item.check_in_photo_url || item.photo,
        checkOutPhoto: item.check_out_photo_url,
        check_in_location: item.check_in_location || item.location || "-",
        check_out_location: item.check_out_location || "-",
        check_in_site: item.check_in_work_site || "-",
        check_out_site: item.check_out_work_site || "-",
        check_in_latitude: item.check_in_latitude,
        check_in_longitude: item.check_in_longitude,
        // Use the correct coordinates based on checkout mode:
        // BSS_INTERNAL auto-checkouts store coords in sales_checkout_ fields;
        // manual checkouts store them in the regular check_out_ fields.
        check_out_mode: item.check_out_mode || null,
        check_out_remarks: item.check_out_remarks || null,
        check_out_latitude: item.check_out_mode === "BSS_INTERNAL"
            ? (item.sales_checkout_latitude || item.check_out_latitude)
            : item.check_out_latitude,
        check_out_longitude: item.check_out_mode === "BSS_INTERNAL"
            ? (item.sales_checkout_longitude || item.check_out_longitude)
            : item.check_out_longitude,
        // sales_checkout_remarks is from the backend serializer — only non-null when BSS_INTERNAL
        sales_checkout_remarks: item.sales_checkout_remarks || null,
        permissions: item.permissions || [],
        consideration_requests: item.consideration_requests || [],
        leavetype: item.Status || item.status || "-",
        onduty_type_check_in: item.onduty_type_check_in || item.onduty_type,
        onduty_type_check_out: item.onduty_type_check_out,
        lead_details: item.lead_details || (item.external_lead_details ? (Array.isArray(item.external_lead_details) ? item.external_lead_details : [item.external_lead_details]) : []),
    }));

    const formatRemarks = (remarks) => {
        if (!remarks) return "No Record";

        if (remarks.includes("Grace - Early Out")) return "Early Out";
        if (remarks.includes("Grace - Late In")) return "Late In";

        return remarks;
    };

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    // Download selected records as CSV
    const downloadSelected = () => {
        if (!selectedRowKeys.length) {
            alert("Please select at least one record to download.");
            return;
        }

        const selectedRows = dataSource.filter((row) =>
            selectedRowKeys.includes(row.key)
        );

        const headers = columns.map((col) => col.title).join(",");

        const rows = selectedRows.map((row) =>
            columns
                .map((col) => {
                    let value =
                        row[col.dataIndex] !== undefined
                            ? row[col.dataIndex]
                            : row[col.key] !== undefined
                                ? row[col.key]
                                : "";

                    // Apply remarks formatting for Excel
                    if (col.dataIndex === "remarks") {
                        value = formatRemarks(value);
                    }

                    return typeof value === "string"
                        ? `"${value.replace(/"/g, '""')}"`
                        : value || "";
                })
                .join(",")
        );

        const csvContent = [headers, ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.href = url;
        link.setAttribute("download", "attendance_selected.csv");

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Handle table pagination changes
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // Clear all filters
    const clearFilters = () => {
        setFilterFromDate(null);
        setFilterToDate(null);
        setFilterStatus("");
        setFilterRemarks("");
        setCurrentPage(1);
    };

    return (
        <>
            {/* Lead Details Modal */}
            <Modal
                open={showLead}
                footer={null}
                onCancel={() => {
                    setShowLead(false);
                    setSelectedLeadDetails(null);
                }}
                width={700}
                title="Lead Details"
            >
                {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
                    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        {selectedLeadDetails.map((lead, index) => (
                            <Descriptions
                                key={index}
                                bordered
                                column={1}
                                size="small"
                                style={{ marginBottom: index < selectedLeadDetails.length - 1 ? 16 : 0 }}
                            >
                                <Descriptions.Item label="Lead Number">
                                    <strong>{lead.lead_no || "-"}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    {lead.customer_name || lead.name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {lead.email || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone Number">
                                    {lead.number || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Project Name">
                                    {lead.project_name || lead.project_id || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span
                                        style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: lead.status
                                                ?.toLowerCase()
                                                .includes("cancel")
                                                ? "#fff2e8"
                                                : "#f6ffed",
                                            color: lead.status
                                                ?.toLowerCase()
                                                .includes("cancel")
                                                ? "#d46b08"
                                                : "#52c41a",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {lead.status || "-"}
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                        No lead details available
                    </div>
                )}
            </Modal>

            <center>
                <h5 style={{ textAlign: "center" }}>Attendance Records</h5>
            </center>

            <center>
                <section className="container mt-4" style={{ margin: "20px", alignItems: "center" }}>
                    <Card
                        style={{
                            padding: "20px",
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Row gutter={[12, 12]} justify="center" style={{ alignItems: "flex-end" }}>
                            <Col xs={24} sm={12} md={5} lg={5}>
                                <label>From Date</label>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={filterFromDate}
                                    onChange={(date) => handleFilterChange(setFilterFromDate, date)}
                                />
                            </Col>

                            <Col xs={24} sm={12} md={5} lg={5}>
                                <label>To Date</label>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={filterToDate}
                                    onChange={(date) => handleFilterChange(setFilterToDate, date)}
                                    disabledDate={(current) =>
                                        current && current > dayjs().endOf("day")
                                    }
                                />
                            </Col>

                            <Col xs={24} sm={12} md={5} lg={5}>
                                <label>Attendance Status</label>
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder="Select Status"
                                    value={filterStatus}
                                    onChange={(value) => handleFilterChange(setFilterStatus, value)}
                                    allowClear
                                >
                                    <Option value="">All Statuses</Option>
                                    <Option value="PRESENT">Present</Option>
                                    <Option value="PERMISSION">Permission</Option>
                                    <Option value="HALFDAY_LEAVE">Halfday</Option>
                                    <Option value="Weekoff">Week Off</Option>
                                    <Option value="Compoff">Comp Off</Option>
                                    <Option value="Sick">Sick Leave</Option>
                                    <Option value="Casual">Casual Leave</Option>
                                    <Option value="ABSENT">Absent</Option>
                                    <Option value="HOLIDAY">Holiday</Option>
                                </Select>
                            </Col>

                            <Col xs={24} sm={12} md={5} lg={5}>
                                <label>Remarks Filter</label>
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder="Select Remark"
                                    value={filterRemarks}
                                    onChange={(value) => handleFilterChange(setFilterRemarks, value)}
                                    allowClear
                                >
                                    <Option value="">All Remarks</Option>
                                    <Option value="LATE IN">Late In</Option>
                                    <Option value="EARLY OUT">Early Out</Option>
                                    <Option value="GRACE - LATE IN">Grace - Late In</Option>
                                    <Option value="GRACE – EARLY OUT">Grace - Early Out</Option>
                                    <Option value="MISPUNCH IN">Mispunch In</Option>
                                    <Option value="MISPUNCH OUT">Mispunch Out</Option>
                                    <Option value="SHORT HOURS">Short Hours</Option>
                                    <Option value="Auto Checkout">Auto Checkout</Option>
                                    <Option value="Technical Issue">Technical Issue</Option>
                                </Select>
                            </Col>

                            <Col xs={24} sm={12} md={4} lg={4} style={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    style={{ width: "100%", marginTop: "24px" }}
                                    onClick={clearFilters}
                                >
                                    Clear Filters
                                </Button>
                            </Col>
                        </Row>
                    </Card>

                    <Button
                        icon={<DownloadOutlined />}
                        block
                        style={{
                            height: "36px",
                            marginTop: "15px",
                            width: "180px",
                            backgroundColor: "#0e497b",
                            color: "white",
                        }}
                        onClick={downloadSelected}
                    >
                        Download Selected
                    </Button>

                    <Card
                        style={{
                            marginTop: "20px",
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >

                        <style>{`
                        .ant-table-wrapper .ant-table-body {
                            transform: rotateX(180deg);
                            overflow-x: auto;
                        }
                        .ant-table-wrapper .ant-table-body table {
                            transform: rotateX(180deg);
                        }
                    `}</style>

                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            loading={loading}
                            bordered
                            pagination={{
                                current: currentPage,
                                position: ["topLeft"],
                                pageSize: pageSize,
                                total: totalRecords,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            scroll={{ x: "max-content", scrollToFirstRowOnChange: true }}
                            rowSelection={rowSelection}
                            onChange={handleTableChange}
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th
                                            {...props}
                                            style={{
                                                backgroundColor: "#0e497b",
                                                color: "white",
                                                marginTop: "20px",
                                            }}
                                        >
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                        />

                    </Card>
                </section>
            </center>

            {/* Request Reason Modal */}
            <Modal
                title="Request Reason"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setRequestReason("");
                }}
                centered
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setIsModalVisible(false);
                            setRequestReason("");
                        }}
                        style={{
                            backgroundColor: "#f0f0f0",
                            color: "#333",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={submitting}
                        onClick={async () => {
                            try {
                                if (!requestReason.trim()) {
                                    alert("Reason for request is mandatory");
                                    return;
                                }

                                setSubmitting(true);

                                const payload = {
                                    attendance_id: attendanceId,
                                    remarks: requestReason,
                                };

                                const res = await axios.post(
                                    `${backUrl}/api/hr/attendance-requests/`,
                                    payload,
                                    {
                                        headers: {
                                            Authorization: `Bearer ${authData?.access}`,
                                            "Content-Type": "application/json",
                                        },
                                    }
                                );

                                alert(res.data?.message || "Request submitted successfully");
                                fetchData();
                                setIsModalVisible(false);
                                setRequestReason("");
                            } catch (error) {
                                console.error(error);
                                const errMsg =
                                    error.response?.data?.message ||
                                    error.response?.data?.detail ||
                                    "Failed to submit request";
                                alert(errMsg);
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                        style={{
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            color: "#fff",
                            borderRadius: "6px",
                        }}
                    >
                        Submit
                    </Button>,
                ]}
            >
                <p>Please enter the reason for your request:</p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter your reason..."
                    value={requestReason}
                    onChange={(e) => setRequestReason(e.target.value)}
                />
            </Modal>
        </>
    );
}